import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import Card, {
	CardBody,
	CardHeader,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../components/bootstrap/Dropdown';
import Icon from '../../components/icon/Icon';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../components/bootstrap/OffCanvas';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import data from '../../common/data/dummyEventsData';
import PaginationButtons, { PER_COUNT } from '../../components/PaginationButtons';
import useSortableData from '../../hooks/useSortableData';
import useDarkMode from '../../hooks/useDarkMode';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/bootstrap/Modal';
import 'react-toastify/dist/ReactToastify.css';
import { addTemplates, deleteTemplate, editTemplates, getTemplates, templateStatusChange } from '../../services/templates';
import Select from '../../components/bootstrap/forms/Select';
import SvgDeleteForever from '../../components/icon/material-icons/DeleteForever';
import Spinner from '../../components/bootstrap/Spinner';

interface ICommonUpcomingEventsProps {
	isFluid?: boolean;
}

const Templates: FC<ICommonUpcomingEventsProps> = ({ isFluid }) => {
	const { themeStatus, darkModeStatus } = useDarkMode();
	const [templateData, setTemplateData] = useState<any[]>([]);
	const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(true);
	const [apiError, setApiError] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(PER_COUNT['5']);
	const [searchKeyword, setSearchKeyword] = useState('');
	const [totalTemplates, setTotalTemplates] = useState(0);
	const { items, requestSort, getClassNamesFor } = useSortableData(data);
	const [upcomingEventsAddModal, setUpcomingEventsAddModal] = useState(false);
	const [upcomingEventsDeleteModal, setUpcomingEventsDeleteModal] = useState(false);
	const [templateId, setTemplateId] = useState("")
	const [selectedType, setSelectedType] = useState('sms'); // Default type

	const handleTypeChange = (e: any) => {
		setSelectedType(e.target.value); // Update selected type
		setCurrentPage(1); // Reset to first page when type changes
	};

	// Fetch templates when the component mounts
	const fetchTemplates = async () => {
		try {
			const body = {
				type: selectedType,
				// status: 1,
				searchKeyword: searchKeyword,
				perPage: perPage,
				page: currentPage
			};

			const response = await getTemplates(body);

			if (response.error) {
				setApiError(response.error);
			} else {
				setTemplateData(response.data || []);
				setTotalTemplates(response.dataCount);
			}
		} catch (error) {
			setApiError('Failed to fetch templates');
		} finally {
			setIsLoadingTemplate(false);
		}
	};

	useEffect(() => {
		fetchTemplates();
	}, [currentPage, perPage, searchKeyword, selectedType]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchKeyword(e.target.value);
		setCurrentPage(1); // Reset to first page when search changes
	};

	// BEGIN :: Upcoming Events
	const [upcomingEventsInfoOffcanvas, setUpcomingEventsInfoOffcanvas] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null); // State for selected template

	const handleUpcomingDetails = (template: Template) => {
		setSelectedTemplate(template); // Set the selected template
		setUpcomingEventsInfoOffcanvas(true); // Open the OffCanvas
	};

	const [editTemplate, setEditTemplate] = useState<Template | null>(null);
	const [upcomingEventsEditOffcanvas, setUpcomingEventsEditOffcanvas] = useState(false);

	type Template = {
		_id: string;
		name: string;
		content: string;
		type: string;
		// Other template properties
	};

	type HandleUpcomingEdit = (template: Template) => void; // Change from User to Template
	const handleUpcomingEdit: HandleUpcomingEdit = (template: Template) => {
		setUpcomingEventsEditOffcanvas(true); // Open the offcanvas
		setEditTemplate(template); // Set the template object
		formik.setValues({
			name: template.name,
			content: template.content,
			file: null, // Reset file field
			type: template.type,
		});
	};


	const handleDelete = async () => {
		try {
			await deleteTemplate(templateId);
			setUpcomingEventsDeleteModal(false)
			fetchTemplates();
			toast.success('Template deleted successfully')
		} catch (error: any) {
			toast.error(error)
		}
	};

	const formik = useFormik({
		initialValues: {
			name: '',
			content: '',
			file: null as File | null,
			type: 'sms',
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Name is required'),
			content: Yup.string().required('Content is required'),
			file: Yup.mixed().nullable(),
			type: Yup.string().required('Type is required'),
		}),
		onSubmit: async (values) => {
			try {
				const file = values.file;
				let attachment = null;
				if (file) {
					attachment = {
						filename: file.name,
						originalname: file.name,
						href: URL.createObjectURL(file),
						contentType: file.type,
						size: file.size,
					};
				}
				const payload = {
					name: values.name,
					content: values.content,
					type: values.type,
					attachments: attachment ? [attachment] : [],
				};
				await editTemplates(editTemplate?._id, payload);
				toast.success('Template updated successfully!');
				fetchTemplates();
				setUpcomingEventsEditOffcanvas(false);
			} catch (error) {
				toast.error('Failed to update template.');
			}
		},
	});


	const addFormik = useFormik({
		initialValues: {
			name: '',
			content: '',
			file: null as File | null, // Explicitly type the file as File or null
			type: 'sms', // Default value for type
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Name is required'),
			content: Yup.string().required('Content is required'),
			file: Yup.mixed().nullable(), // Optional file validation
			type: Yup.string().required('Type is required'), // Validation for type
		}),
		onSubmit: async (values, formikHelpers) => {
			try {
				const file = values.file;

				// Process file if present
				let attachment = null;
				if (file) {
					attachment = {
						filename: file.name,
						originalname: file.name,
						href: URL.createObjectURL(file), // Temporary URL
						contentType: file.type,
						size: file.size,
					};
				}

				// Prepare the payload
				const payload = {
					name: values.name,
					content: values.content,
					type: values.type, // Add the type field
					attachments: attachment ? [attachment] : [],
				};

				// API call to create the template
				await addTemplates(payload);
				toast.success('Template added successfully!');
				setUpcomingEventsAddModal(false); // Close the modal on success
				formikHelpers.resetForm();
				fetchTemplates(); // Refresh the list
			} catch (error) {
				toast.error('Failed to add template. Please try again.');
			}
		},
	});
	const handleStatusChange = async (id: string, status: number) => {
		const result = await templateStatusChange(id, { status: status }); // Call your API function with id and status
		if (result.error) {
			toast.error(result.error);
		} else {
			toast.success('Template status changed successfully!');
			fetchTemplates();
		}
	};

	return (
		<>
			{isLoadingTemplate && (
                <div
                    style={{
                        position: 'fixed', // Fixed position to cover the entire viewport
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)', // Black background with 50% opacity
                        display: 'flex', // Use flexbox to center the spinner
                        justifyContent: 'center', // Center horizontally
                        alignItems: 'center', // Center vertically
                        zIndex: 9999, // Ensure it appears above other content
                    }}
                >
                    <Spinner size="3rem" /> {/* Adjust size as needed */}
                </div>
            )}
			<Card stretch={isFluid}>
				<CardHeader >
					<div className='d-flex justify-content-between align-items-center w-100'>
						{/* Search Input */}
						<div className='col-md-3 d-flex align-items-center justify-content-center gap-3'>
							<Input
								placeholder='Search templates...'
								value={searchKeyword}
								onChange={handleSearchChange}
								type='search'
								style={{ height: '38px', lineHeight: '38px' }}
							// icon='Search'
							/>
							<FormGroup id="type">
								<Select
									id="type"
									size="lg"
									ariaLabel=""
									placeholder="Select Type"
									list={[
										{ label: 'SMS', value: 'sms' },
										{ label: 'Email', value: 'email' },
										{ label: 'Push', value: 'push' },
									]} // Options for the type field
									className={classNames('rounded-1', {
										'bg-white': !darkModeStatus,
									})}
									style={{ height: '38px' }}
									onChange={handleTypeChange} // Handle type change
									value={selectedType}
								/>
							</FormGroup>
						</div>
						<Button
							color={darkModeStatus ? 'light' : 'dark'}
							isLight
							icon='Add'
							onClick={() => setUpcomingEventsAddModal(true)}
						>
							Add Template
						</Button>
					</div>
				</CardHeader>
				<CardBody className='table-responsive' isScrollable={isFluid}>
					<table className='table table-modern'>
						<thead>
							<tr>
								<td aria-labelledby='Image' style={{ width: 60 }} />
								<th
									onClick={() => requestSort('date')}
									className='cursor-pointer text-decoration-underline'>
									Date / Time{' '}
									<Icon
										size='lg'
										className={getClassNamesFor('date')}
										icon='FilterList'
									/>
								</th>
								<th>Name</th>
								<th>Content</th>
								<th>Type</th>
								<th>Created Date</th>
								<th>Status</th>
								<td aria-labelledby='Actions' />
							</tr>
						</thead>
						<tbody>
							{templateData?.length > 0 && templateData?.map((item) => (
								<tr key={item._id}>
									<td>
										<Button
											isOutline={!darkModeStatus}
											color='dark'
											isLight={darkModeStatus}
											className={classNames({
												'border-light': !darkModeStatus,
											})}
											icon='Info'
											onClick={() => handleUpcomingDetails(item)}
											aria-label='Detailed information'
										/>
									</td>
									<td>
										<div className='d-flex align-items-center'>
											<span
												className={classNames(
													'badge',
													'border border-2',
													[`border-${themeStatus}`],
													'rounded-circle',
													'bg-success',
													'p-2 me-2',
													`bg-${item.status.color}`,
												)}>
												<span className='visually-hidden'>
													{item.fName}
												</span>
											</span>
											<span className='text-nowrap'>
												{dayjs(item.createdAt).format('MMM Do YYYY, h:mm a')}
											</span>
										</div>
									</td>
									<td>
										<div>
											{/* <div>{item?.customer?.name}</div> */}
											<div className='small text-muted'>
												{item?.name}
											</div>
										</div>
									</td>
									<td>
										{item.content}
									</td>
									<td>
										{item.type}
									</td>
									<td>
										{dayjs(item.createdAt).format('MMM Do YYYY, h:mm a')}
									</td>
									<td>
										<Dropdown>
											<DropdownToggle hasIcon={false}>
												<Button
													isLink
													color={item.status === 1 ? 'success' : 'danger'} // Current status color
													icon='Circle'
													className='text-nowrap'>
													{item.status === 1 ? 'Active' : 'Inactive'} {/* Current status name */}
												</Button>
											</DropdownToggle>
											<DropdownMenu>
												{/* Map over the statuses for Active and Inactive */}
												{[0, 1].map((status) => (
													<DropdownItem
														key={status}
														onClick={() => handleStatusChange(item._id, status)} // Call handler to change status
													>
														<div>
															<Icon
																icon='Circle'
																color={status === 1 ? 'success' : 'danger'}
															/>
															{status === 1 ? 'Active' : 'Inactive'}
														</div>
													</DropdownItem>
												))}
											</DropdownMenu>
										</Dropdown>
									</td>


									<td className='d-flex gap-3'>
										<Button
											isOutline={!darkModeStatus}
											color='dark'
											isLight={darkModeStatus}
											className={classNames('text-nowrap', {
												'border-light': !darkModeStatus,
											})}
											icon='Edit'
											onClick={() => handleUpcomingEdit(item)}>
											Edit
										</Button>
										<Button
											isOutline={!darkModeStatus}
											color="danger"
											isLight={darkModeStatus}
											className={classNames('text-nowrap', {
												'border-light': !darkModeStatus,
											})}
											icon="Delete"
											onClick={() => {
												setUpcomingEventsDeleteModal(true); // Open the modal
												setTemplateId(item._id); // Set the template ID for deletion
											}}
										/>
									</td>
								</tr>
							))}
						</tbody >
					</table >
				</CardBody >
				<PaginationButtons
					setCurrentPage={setCurrentPage}
					currentPage={currentPage}
					perPage={perPage}
					setPerPage={setPerPage}
					data={templateData}
					label="templateData"
					totalItems={totalTemplates}
				/>
			</Card >

			<OffCanvas
				setOpen={setUpcomingEventsInfoOffcanvas}
				isOpen={upcomingEventsInfoOffcanvas}
				titleId='upcomingDetails'
				placement='bottom'>
				<OffCanvasHeader setOpen={setUpcomingEventsInfoOffcanvas}>
					<OffCanvasTitle id='upcomingDetails'>Template Details</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					{selectedTemplate ? (
						<div>
							<h5>Name: {selectedTemplate.name}</h5>
							<h5>Content: {selectedTemplate.content}</h5>
							<h5>Type: {selectedTemplate.type}</h5>
							{/* Add any other template details you want to display */}
						</div>
					) : (
						<p>No template selected.</p>
					)}
				</OffCanvasBody>
			</OffCanvas>

			<OffCanvas
				setOpen={setUpcomingEventsEditOffcanvas}
				isOpen={upcomingEventsEditOffcanvas}
				titleId="templateEdit"
				isBodyScroll
				placement="end"
			>
				<OffCanvasHeader setOpen={setUpcomingEventsEditOffcanvas}>
					<OffCanvasTitle id="templateEdit">Edit Template</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					<div className="row g-4">
						<div className="col-12">
							<FormGroup id="name" label="Name" isFloating>
								<Input
									placeholder="Name"
									name="name"
									onChange={formik.handleChange}
									value={formik.values.name}
									onBlur={formik.handleBlur}
									isTouched={formik.touched.name}
									invalidFeedback={formik.errors.name}
								/>
							</FormGroup>
						</div>
						<div className="col-12">
							<FormGroup id="content" label="Content" isFloating>
								<Input
									placeholder="Content"
									name="content"
									onChange={formik.handleChange}
									value={formik.values.content}
									onBlur={formik.handleBlur}
									isTouched={formik.touched.content}
									invalidFeedback={formik.errors.content}
								/>
							</FormGroup>
						</div>
						<div className="col-12">
							<FormGroup id="file" label="Attachment">
								<Input
									type="file"
									name="file"
									onChange={(e: any) => {
										const file = e.currentTarget.files?.[0];
										formik.setFieldValue("file", file);
									}}
									isTouched={formik.touched.file}
									invalidFeedback={formik.errors.file}
								/>
							</FormGroup>
						</div>
						<div className="col-12">
							<FormGroup id="type" label="Type">
								<Select
									id="type"
									size="lg"
									ariaLabel="Type"
									placeholder="Select Type"
									list={[
										{ label: 'SMS', value: 'sms' },
										{ label: 'Email', value: 'email' },
										{ label: 'Push', value: 'push' },
									]} // Options for the type field
									className={classNames('rounded-1', {
										'bg-white': !darkModeStatus,
									})}
									onChange={(e: { target: { value: any } }) =>
										formik.setFieldValue('type', e.target.value)
									}
									value={formik.values.type}
								/>
							</FormGroup>
						</div>
					</div>
				</OffCanvasBody>
				<div className="row m-0">
					<div className="col-12 p-3">
						<Button
							color="info"
							className="w-100"
							onClick={() => {
								formik.handleSubmit();
							}}
						>
							Save
						</Button>
					</div>
				</div>
			</OffCanvas>

			<Modal
				setIsOpen={setUpcomingEventsAddModal}
				isOpen={upcomingEventsAddModal}
				titleId="templateAdd"
				isCentered
				isScrollable
				size="lg"
			>
				<ModalHeader setIsOpen={setUpcomingEventsAddModal}>
					<h5 id="templateAdd">Add Template</h5>
				</ModalHeader>
				<ModalBody>
					<div className="row g-4">
						<div className="col-6">
							<FormGroup id="name" label="Name" isFloating>
								<Input
									placeholder="Name"
									name="name"
									onChange={addFormik.handleChange}
									value={addFormik.values.name}
									onBlur={addFormik.handleBlur} // Add onBlur for touched state
									isTouched={addFormik.touched.name}
									invalidFeedback={addFormik.errors.name}
								/>
							</FormGroup>
						</div>
						<div className="col-6">
							<FormGroup id="content" label="Content" isFloating>
								<Input
									placeholder="Content"
									name="content"
									onChange={addFormik.handleChange}
									value={addFormik.values.content}
									onBlur={addFormik.handleBlur}
									isTouched={addFormik.touched.content}
									invalidFeedback={addFormik.errors.content}
								/>
							</FormGroup>
						</div>
						<div className="col-6">
							<FormGroup id="file" label="Attachment">
								<Input
									type="file"
									name="file"
									onChange={(e: any) => {
										const file = e.currentTarget.files?.[0];
										addFormik.setFieldValue("file", file);
									}}
									isTouched={addFormik.touched.file}
									invalidFeedback={addFormik.errors.file}
								/>
							</FormGroup>
						</div>
						<div className="col-6">
							<FormGroup id="type" label="Type">
								<Select
									id="type"
									size="lg"
									ariaLabel="Type"
									placeholder="Select Type"
									list={[
										{ label: 'SMS', value: 'sms' },
										{ label: 'Email', value: 'email' },
										{ label: 'Push', value: 'push' },
									]} // Options for the type field
									className={classNames('rounded-1', {
										'bg-white': !darkModeStatus,
									})}
									onChange={(e: { target: { value: any } }) =>
										addFormik.setFieldValue('type', e.target.value)
									}
									value={addFormik.values.type}
								/>
								{/* {addFormik.touched.type && addFormik.errors.type && (
									<div className="invalid-feedback d-block">
										{addFormik.errors.type}
									</div>
								)} */}
							</FormGroup>
						</div>
					</div>
				</ModalBody>
				<ModalFooter className="bg-transparent">
					<Button
						color="info"
						className="w-100"
						onClick={addFormik.handleSubmit} // Use addFormik's handleSubmit
					>
						Save
					</Button>
				</ModalFooter>
			</Modal>
			<Modal
				setIsOpen={setUpcomingEventsDeleteModal}
				isOpen={upcomingEventsDeleteModal}
				titleId="templateAdd"
				isCentered
				isScrollable
				size="lg"
			>
				<ModalHeader setIsOpen={setUpcomingEventsDeleteModal}>
					<h5 id="templateAdd">Delete Template</h5>
				</ModalHeader>
				<ModalBody>
					<div className="row g-4 text-center">
						<div className="col-12">
							<SvgDeleteForever
								style={{
									color: darkModeStatus ? '#FFFFFF' : '#FF0000', // Adjust colors dynamically
									height: '50px', // Make the icon larger
									width: '50px',
								}}
							/>
						</div>
						<div className="col-12">
							<p className="mt-3">Are you sure you want to delete this template?</p>
						</div>
					</div>
				</ModalBody>
				<ModalFooter className="bg-transparent d-flex justify-content-between">
					<Button
						isOutline
						color="info"
						className="me-2 flex-grow-1"
						onClick={() => setUpcomingEventsDeleteModal(false)} // Close the modal
					>
						Cancel
					</Button>
					<Button
						color="info"
						className="ms-2 flex-grow-1"
						onClick={handleDelete} // Trigger delete action
					>
						Confirm
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default Templates;